import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import './Login.css';

const PASSWORD_STRONG_REGEX = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!token) {
      setMessage({ type: 'error', text: 'Reset link is invalid.' });
      return;
    }

    if (!PASSWORD_STRONG_REGEX.test(newPassword)) {
      setMessage({
        type: 'error',
        text: 'Use at least 8 characters with letters, numbers, and symbols',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/api/auth/reset-password', {
        token,
        newPassword,
        confirmPassword,
      });

      if (response.data.success) {
        setDone(true);
        setMessage({
          type: 'success',
          text: response.data.message || 'Password reset successfully. You can now log in.',
        });
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Could not reset password',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Could not reset password. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <button
            type="button"
            className="login-back-home"
            onClick={() => navigate('/login')}
          >
            ← Back to login
          </button>
          <img className="login-logo" src="/atozasmail-logo.png" alt="ATOZASMail" />
          <h2>Reset password</h2>
          <p className="subtitle">Choose a new password for your ATOZASMail account.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="newPassword">New password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="newPassword"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (message.text) setMessage({ type: '', text: '' });
              }}
              placeholder="Enter new password"
              className="form-input"
              required
              disabled={loading || done}
              autoFocus
              autoComplete="new-password"
            />
            <small className="form-hint">
              At least 8 characters with letters, numbers, and symbols
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (message.text) setMessage({ type: '', text: '' });
              }}
              placeholder="Re-enter new password"
              className="form-input"
              required
              disabled={loading || done}
              autoComplete="new-password"
            />
          </div>

          <label className="forgot-show-password">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              disabled={loading || done}
            />
            Show password
          </label>

          {message.text && (
            <div className={`message ${message.type}`}>{message.text}</div>
          )}

          <button type="submit" className="submit-button" disabled={loading || done}>
            {loading ? 'Please wait...' : done ? 'Password updated' : 'Reset password'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Need a new link?{' '}
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="toggle-button"
              disabled={loading}
            >
              Request again
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
